import React, { useEffect, useState } from "react";

import { formatFractionInput, queryIsLoading, scrollPage, useGetFootprintingFarmFootprint, useTranslation, useUserContext } from "utils";

import { AggregatedFarmFootprint } from "models";

import { Card, ErrorBoundary, ListCard, Message as message } from "components";

import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { FarmFormFlow } from "pages/app/footprinting/components/formFlows/FarmFormFlow";

interface FootprintingFarmReviewProps {
  year?: number;
}

export const getLandUseChangeSlugForFarmFootprint = (farmFootprint?: AggregatedFarmFootprint): string => {
  if (!farmFootprint || farmFootprint.landUseChangeUsage.length === 0) return "";

  if (farmFootprint.landUseChangeUsage.some((luc) => luc.isUnknown)) return "landUseForm.reviewRadioButton.dontKnow";

  if (farmFootprint.landUseChangeUsage.some((luc) => luc.hasLandChanged === "noLUC")) return "landUseForm.reviewRadioButton.no";

  return "landUseForm.reviewRadioButton.yes";
};

export const displayRadioAnswerSlug = (isActive: boolean | null) => (isActive === null ? "" : `radioAnswer.${isActive}`);

export const FootprintingFarmReview = ({ year }: FootprintingFarmReviewProps) => {
  const { t } = useTranslation("footprinting");
  const { user } = useUserContext();
  const farmId = user?.farm.id ?? undefined;
  const [showFormFlow, setShowFormFlow] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>();

  const farmFootprintQuery = useGetFootprintingFarmFootprint(farmId, year);

  const handleExit = () => {
    setShowFormFlow(false);
    setActiveStep(undefined);
    message({ content: t("progressSaved"), icon: "tick" });
  };

  useEffect(() => {
    if (Number.isInteger(activeStep)) {
      setShowFormFlow(true);
    }
  }, [activeStep]);

  useEffect(() => {
    scrollPage();
  }, []);

  const loading = queryIsLoading(farmFootprintQuery);

  return (
    <>
      <Card className="padding-125">
        <ReviewPageTitle title={t("review.farm.title")} iconName="klim/farm" isLoading={loading} />
        {/* Farm soil form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.farm.soil")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(0) }}
          dataPairs={[
            {
              label: t("soilForm.soilTypeLabel"),
              value: farmFootprintQuery.data?.footprint?.overallSoilType
                ? t(`soilForm.soilTypeOptions.${farmFootprintQuery.data?.footprint?.overallSoilType}`)
                : "",
            },
            {
              label: t("soilForm.soilSaturationLabel"),
              value: farmFootprintQuery.data?.footprint?.averageSoilSaturation
                ? t(`soilForm.soilSaturationOptions.${farmFootprintQuery.data?.footprint?.averageSoilSaturation}`)
                : "",
            },
          ]}
        />
        {/* Farm electricity form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.farm.electricity")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(1) }}
          dataPairs={[
            { label: t("electricityForm.question.total"), value: `${farmFootprintQuery.data?.footprint?.electricityKwh} kWh` },
            { label: t("electricityForm.question.renewables"), value: `${formatFractionInput(farmFootprintQuery.data?.footprint?.renewablesPct ?? 0)} %` },
          ]}
        />
        {/* Farm waste form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.farm.waste")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(2) }}
          dataPairs={[
            { label: t("wasteForm.wasteQuestionLabels"), value: `${farmFootprintQuery.data?.footprint?.wasteKgs} kg` },
            { label: t("wasteForm.wasteWaterQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.wasteWaterM3} m³` },
          ]}
        />
        {/* Farm fuel form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.farm.fuel")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(3) }}
          dataPairs={[
            { label: t("fuelForm.question.dieselQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.dieselL} L` },
            { label: t("fuelForm.question.petrolQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.petrolL} L` },
            { label: t("fuelForm.question.naturalGasQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.naturalGasM3} m³` },
            { label: t("fuelForm.question.heatingOilQuestionLabel"), value: `${farmFootprintQuery.data?.footprint?.heatingOilL} L` },
          ]}
        />
        {/* Farm refrigerants form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.farm.cooling")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(4) }}
          dataPairs={[
            {
              label: t("coolingForm.question.refrigerantsUsed"),
              value: t(displayRadioAnswerSlug(farmFootprintQuery.data?.footprint?.refrigerantsActive ?? null)),
              children: farmFootprintQuery.data?.footprint?.refrigerantsActive
                ? farmFootprintQuery.data?.footprint?.refrigerantUsage.map((usedRefrigerant, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("coolingForm.question.title") + (index + 1)}
                      dataPairs={[
                        {
                          label: t("coolingForm.question.refrigerantType"),
                          value: usedRefrigerant.refrigerantType ?? "",
                        },
                        {
                          label: t("coolingForm.question.refrigerantAmount"),
                          value: `${usedRefrigerant.amountUsedKg} kg`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Farm transport form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.farm.transport")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(5) }}
          dataPairs={[
            {
              label: t("farmTransportForm.question.radioQuestion"),
              value: t(displayRadioAnswerSlug(farmFootprintQuery.data?.footprint?.purchasesActive ?? null)),
              children: farmFootprintQuery.data?.footprint?.purchasesActive
                ? farmFootprintQuery.data?.footprint?.purchases.map((i, index) => (
                    <ListCard
                      key={index}
                      isSublist
                      isLoading={loading}
                      title={t("farmTransportForm.retailer", { count: index + 1 })}
                      dataPairs={[
                        {
                          label: t("farmTransportForm.question.retailerNameQuestionLabel"),
                          value: i.retailerName ?? "",
                        },
                        {
                          label: t("farmTransportForm.question.countryQuestionLabel"),
                          value: i.retailerCountryCode ?? "",
                        },
                        {
                          label: t("farmTransportForm.question.postcodeQuestionLabel"),
                          value: i.retailerPostCode ?? "",
                        },
                        {
                          label: t("farmTransportForm.question.mineralFertilizerQuestionLabel"),
                          value: `${formatFractionInput(i.mineralFertilizerPct)} %`,
                        },
                        {
                          label: t("farmTransportForm.question.organicFertilizerQuestionLabel"),
                          value: `${formatFractionInput(i.organicFertilizerPct)} %`,
                        },
                        {
                          label: t("farmTransportForm.question.seedsQuestionLabel"),
                          value: `${formatFractionInput(i.seedsPct)} %`,
                        },
                        {
                          label: t("farmTransportForm.question.plantProtectionQuestionLabel"),
                          value: `${formatFractionInput(i.plantProtectionPct)} %`,
                        },
                      ]}
                    />
                  ))
                : null,
            },
          ]}
        />
        {/* Land Use Change form */}
        <ListCard
          className="margin-bottom-100"
          isOutlined
          isLoading={loading}
          title={t("navigation.farm.landuse")}
          titleButton={{ label: t("review.edit"), onClick: () => setActiveStep(6) }}
          dataPairs={[
            {
              label: t("landUseForm.question.general"),
              value: t(getLandUseChangeSlugForFarmFootprint(farmFootprintQuery.data?.footprint)),
              children:
                (farmFootprintQuery.data?.footprint?.landUseChangeUsage.length ?? 0) > 0
                  ? farmFootprintQuery.data?.footprint?.landUseChangeUsage
                      .filter((luc) => luc.hasLandChanged === "yesLUC" && (luc.areaConvertedHa ?? 0) > 0)
                      .map((luc, index) => (
                        <ListCard
                          key={luc.id}
                          isSublist
                          isLoading={loading}
                          title={t("landUseForm.question.title") + (index + 1)}
                          dataPairs={[
                            {
                              label: t("landUseForm.question.previousLandChange"),
                              value: luc.previousLandUseType ?? "",
                            },
                            {
                              label: t("landUseForm.question.conversionYear"),
                              value: `${luc.yearOfConversion}`,
                            },
                            {
                              label: t("landUseForm.question.areaConverted"),
                              value: `${luc.areaConvertedHa} Ha`,
                            },
                          ]}
                        />
                      ))
                  : null,
            },
          ]}
        />
      </Card>
      <ErrorBoundary>
        <FarmFormFlow
          readOnly={farmFootprintQuery.data?.footprint?.isSubmitted}
          showFormFlow={showFormFlow}
          initialStep={activeStep}
          onExit={handleExit}
          farmId={farmId ?? ""}
          year={year ?? 0}
          editMode
        />
      </ErrorBoundary>
    </>
  );
};
